import {
  Badge,
  BlockStack,
  Box,
  Card,
  FormLayout,
  InlineStack,
  Select,
  Text,
  TextField,
} from '@shopify/polaris';
import { ColorPickerField } from './ColorPickerField';
import { ResourcePicker } from './ResourcePicker';
import { buildLinkPreview } from '../shared/useNotificationFormState';
import type {
  ImageCrop,
  NotificationTargetType,
  PickerItem,
} from '@/queries/notifications';

const TARGET_OPTIONS: { label: string; value: NotificationTargetType }[] = [
  { label: 'يفتح التطبيق على الـ Home', value: 'none' },
  { label: 'منتج (Product)', value: 'product' },
  { label: 'مجموعة (Collection)', value: 'collection' },
  { label: 'طلب (Order ID)', value: 'order' },
  { label: 'رابط خارجي (URL)', value: 'url' },
];

const CROP_OPTIONS: { label: string; value: ImageCrop }[] = [
  { label: 'تلقائي — الأصلي بدون قص', value: 'auto' },
  { label: 'مربع (1:1) — أنسب للمنتجات', value: 'square' },
  { label: 'عريض (16:9) — أنسب للـ banners', value: 'landscape' },
  { label: 'طولي (3:4) — أنسب للـ fashion', value: 'portrait' },
  { label: 'إخفاء الصورة — title + body فقط', value: 'hidden' },
];

export interface MediaCardProps {
  imageUrl: string;
  imageCrop: ImageCrop;
  imageBgEnabled: boolean;
  imageBgColor: string;
  targetType: NotificationTargetType;
  targetValue: string;
  targetSearch: string;
  targetThumbnail: string;
  effectiveImageUrl: string | undefined;
  products: { items: PickerItem[]; isLoading: boolean };
  collections: { items: PickerItem[]; isLoading: boolean };
  onImageUrlChange: (v: string) => void;
  onImageCropChange: (v: ImageCrop) => void;
  onImageBgEnabledChange: (v: boolean) => void;
  onImageBgColorChange: (v: string) => void;
  onTargetTypeChange: (v: NotificationTargetType) => void;
  onTargetValueChange: (v: string) => void;
  onTargetSearchChange: (v: string) => void;
  onPickResource: (item: PickerItem) => void;
  onClearTarget: () => void;
}

export function MediaCard(props: MediaCardProps) {
  const {
    imageUrl,
    imageCrop,
    imageBgEnabled,
    imageBgColor,
    targetType,
    targetValue,
    targetSearch,
    targetThumbnail,
    effectiveImageUrl,
    products,
    collections,
    onImageUrlChange,
    onImageCropChange,
    onImageBgEnabledChange,
    onImageBgColorChange,
    onTargetTypeChange,
    onTargetValueChange,
    onTargetSearchChange,
    onPickResource,
    onClearTarget,
  } = props;

  const linkPreview = buildLinkPreview(targetType, targetValue);

  const renderTargetValue = () => {
    if (targetType === 'none') return null;
    if (targetType === 'product') {
      return (
        <ResourcePicker
          label="المنتج"
          placeholder="ابحث في المنتجات…"
          items={products.items}
          loading={products.isLoading}
          search={targetSearch}
          onSearchChange={onTargetSearchChange}
          value={targetValue}
          onPick={onPickResource}
          onClear={onClearTarget}
        />
      );
    }
    if (targetType === 'collection') {
      return (
        <ResourcePicker
          label="المجموعة"
          placeholder="ابحث في المجموعات…"
          items={collections.items}
          loading={collections.isLoading}
          search={targetSearch}
          onSearchChange={onTargetSearchChange}
          value={targetValue}
          onPick={onPickResource}
          onClear={onClearTarget}
        />
      );
    }
    if (targetType === 'order') {
      return (
        <TextField
          label="Order ID"
          autoComplete="off"
          value={targetValue}
          onChange={onTargetValueChange}
          placeholder="مثل: 5012345678"
        />
      );
    }
    return (
      <TextField
        label="URL"
        autoComplete="off"
        value={targetValue}
        onChange={onTargetValueChange}
        placeholder="https://… أو padelneeds://…"
        type="url"
      />
    );
  };

  return (
    <Card>
      <BlockStack gap="400">
        <InlineStack align="space-between" blockAlign="center">
          <Text as="h2" variant="headingMd">
            الصورة والوجهة
          </Text>
          <Text as="span" variant="bodySm" tone="subdued">
            الخطوة 2 من 4
          </Text>
        </InlineStack>

        <FormLayout>
          <Select
            label="لما المستخدم يدوس، يفتح إيه؟"
            options={TARGET_OPTIONS}
            value={targetType}
            onChange={(v) => onTargetTypeChange(v as NotificationTargetType)}
          />
          {renderTargetValue()}

          {linkPreview ? (
            <Box background="bg-surface-secondary" padding="300" borderRadius="200">
              <Text as="p" variant="bodySm" tone="subdued">
                Deep link: <code>{linkPreview}</code>
              </Text>
            </Box>
          ) : null}

          <TextField
            label="رابط الصورة (اختياري)"
            value={imageUrl}
            onChange={onImageUrlChange}
            autoComplete="off"
            type="url"
            helpText={
              !imageUrl.trim() && targetThumbnail
                ? '⚡ هتُستخدم صورة المنتج/المجموعة المختارة تلقائيًا.'
                : 'تظهر داخل الإشعار على Android فورًا و iOS بعد الـ NSE.'
            }
          />

          <Select
            label="حجم/شكل الصورة (iOS expanded)"
            options={CROP_OPTIONS}
            value={imageCrop}
            onChange={(v) => onImageCropChange(v as ImageCrop)}
            helpText="يحدد aspect ratio في الـ expanded view. الـ banner thumbnail بيظل صغير."
          />

          <ColorPickerField
            enabled={imageBgEnabled}
            onEnabledChange={onImageBgEnabledChange}
            value={imageBgColor}
            onChange={onImageBgColorChange}
          />

          {effectiveImageUrl && !imageUrl.trim() && targetThumbnail ? (
            <Box paddingBlockStart="100">
              <InlineStack gap="200" blockAlign="center">
                <Badge tone="info">{`صورة من ${targetType} المختار`}</Badge>
                <Text as="span" variant="bodySm" tone="subdued">
                  ممكن تغيرها من حقل "رابط الصورة" فوق
                </Text>
              </InlineStack>
            </Box>
          ) : null}
        </FormLayout>
      </BlockStack>
    </Card>
  );
}
